import React, { useCallback, useEffect, useRef, useState } from "react";
import { Flex, Box, SimpleGrid, AspectRatio, VisuallyHidden, Spinner } from "@chakra-ui/react";
import Image from "next/image";
import { useRouter } from "next/router";
import { blogsPerPageLimit, type BlogCategory, type BlogSimple } from "lib/sanity";
import { Page } from "components/page";
import { Section, sectionSpacingY } from "components/section";
import { ResourceCard } from "components/resource-card";
import { formatDate, getPageMetaImage } from "utils";
import { FadeIn } from "ui/animations";
import { TextTitle } from "ui/text";
import { Link } from "ui/link";
import { handleClientError } from "lib/errors";
import { blogBlurDataUrl } from "../../constants/blur-data-urls";
import { BlogSidebar, defaultCategory } from "./blog-sidebar";
import { blogService } from "./blog-service";
import { FeaturedResourceCard } from "./featured-resource-card";

function getPageBlogCount(page: number, categoryBlogCount: number) {
  return categoryBlogCount - blogsPerPageLimit * (page - 1);
}

function LoadingSpinner() {
  return (
    <>
      <Spinner emptyColor="brand.backgroundSecondary.light" color="brand.accent.secondary" />
      <VisuallyHidden>Loading more posts...</VisuallyHidden>
    </>
  );
}

type UseBlogDirectoryProps = {
  allPosts: BlogSimple[];
  initialCategory: BlogCategory;
  initialCategoryBlogCount: number;
  initialPage: number;
};

type BlogDirectoryTemplateProps = {
  metaTitle: string;
  metaDescription: string;
  metaImageUrl?: string;
  categories: BlogCategory[];
} & UseBlogDirectoryProps;

type LoadingState = { status: "idle" } | { status: "loading-category" } | { status: "loading-more-posts" };

function useBlogDirectory(initialProps: UseBlogDirectoryProps) {
  const router = useRouter();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: "idle" });
  const [currentPage, setCurrentPage] = useState(initialProps.initialPage ?? 1);
  const [filteredPosts, setFilteredPosts] = useState(initialProps.allPosts);
  const [activeCategory, setActiveCategory] = useState(initialProps.initialCategory);
  const [categoryBlogCount, setCategoryBlogCount] = useState(initialProps.initialCategoryBlogCount);
  const [pageBlogCount, setPageBlogCount] = useState(
    getPageBlogCount(initialProps.initialPage, initialProps.initialCategoryBlogCount),
  );

  // Update url params and scroll to top when changing category
  const handleCategoryChange = useCallback(
    async (category: BlogCategory) => {
      setLoadingState({ status: "loading-category" });

      try {
        const [newPosts, newBlogCount] = await Promise.all([
          blogService.fetchPostsByCategory(category.slug),
          blogService.fetchBlogCount(category.slug),
        ]);

        setActiveCategory(category);
        setCurrentPage(1);
        setFilteredPosts(newPosts);
        setCategoryBlogCount(newBlogCount);
        setPageBlogCount(newBlogCount);
        window.scrollTo({ top: 0, behavior: "smooth" });

        await router.push(`/blog/category/${category.slug}/page/1`, undefined, {
          shallow: true,
        });
      } catch (error) {
        handleClientError(`Error changing blog category: ${error}`);
      } finally {
        setLoadingState({ status: "idle" });
      }
    },
    [router],
  );

  // Load more posts when the load more ref comes into view
  const handleLoadMore = useCallback(async () => {
    setLoadingState({ status: "loading-more-posts" });

    try {
      const nextPage = currentPage + 1;
      const newPosts = await blogService.fetchPostsByCategory(activeCategory.slug, nextPage);

      setFilteredPosts((previous: BlogSimple[]) => [...previous, ...newPosts]);
      setCurrentPage(nextPage);

      await router.push(`/blog/category/${activeCategory.slug}/page/${nextPage}`, undefined, {
        shallow: true,
      });
    } catch (error) {
      handleClientError(`Error loading more posts: ${error}`);
    } finally {
      setLoadingState({ status: "idle" });
    }
  }, [activeCategory, currentPage, router]);

  // Determine when the load more ref comes into view and trigger handleLoadMore
  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        const target = entries[0];
        if (target.isIntersecting && loadingState.status === "idle") {
          await handleLoadMore();
        }
      },
      { threshold: 0.5 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loadingState, handleLoadMore]);

  return {
    pageBlogCount,
    categoryBlogCount,
    loadingState,
    filteredPosts,
    activeCategory,
    loadMoreRef,
    handleLoadMore,
    handleCategoryChange,
  };
}

export function BlogDirectoryTemplate({
  metaTitle,
  metaDescription,
  metaImageUrl,
  allPosts,
  initialCategoryBlogCount,
  categories,
  initialCategory = defaultCategory,
  initialPage,
}: BlogDirectoryTemplateProps) {
  const {
    pageBlogCount,
    categoryBlogCount,
    loadingState,
    filteredPosts,
    activeCategory,
    loadMoreRef,
    handleCategoryChange,
  } = useBlogDirectory({
    allPosts,
    initialCategory,
    initialCategoryBlogCount,
    initialPage,
  });
  const featuredPost = filteredPosts?.[0];

  return (
    <Page
      title={`${metaTitle}${activeCategory.slug === "all" ? "" : ` - ${activeCategory.name}`}`}
      description={metaDescription}
      image={getPageMetaImage({ src: metaImageUrl, alt: metaTitle })}
    >
      <Section my={{ base: "tiny", sm: "big", lg: "bigger" }}>
        <VisuallyHidden>
          <TextTitle size="lg" titleAs="h1" title={`The Hightouch Blog - ${activeCategory.name}`} />
        </VisuallyHidden>

        <Flex gap="bigger" mt={{ base: 0, lg: "biggest" }} pb={sectionSpacingY}>
          <BlogSidebar
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />

          <Box flex={1} pos="relative">
            {featuredPost && (
              <FadeIn duration={0.2}>
                <FeaturedResourceCard
                  title={featuredPost.title}
                  href={`/blog/${featuredPost.slug}`}
                  excerpt={featuredPost.excerpt}
                  publishedAt={featuredPost.publishedAt}
                  image={
                    <AspectRatio ratio={2 / 1} pos="relative" width="100%">
                      <Image
                        priority
                        fill
                        sizes="100%"
                        src={`${featuredPost.imageUrl}?w=1200`}
                        alt="" // Treat images as decorative
                        placeholder="blur"
                        blurDataURL={blogBlurDataUrl}
                      />
                    </AspectRatio>
                  }
                />
              </FadeIn>
            )}

            <SimpleGrid mt={{ base: "bigger", md: "bigger" }} columns={{ base: 1, md: 2, xl: 3 }} gap="small">
              {filteredPosts?.slice(1).map((post, index) => (
                <FadeIn key={post.slug} duration={0.2}>
                  <ResourceCard
                    height="100%"
                    maxW={{ base: "cardMaxW", md: "100%" }}
                    mx="auto"
                    href={`/blog/${post.slug}`}
                    linkAriaLabel={`Read ${post.title}.`}
                    eyebrow={formatDate(post.publishedAt)}
                    title={post.title}
                    // Only use h2 for the first 6 posts to avoid overloading the heading hierarchy
                    titleAs={index < 6 ? "h2" : "p"}
                    excerpt={post.excerpt}
                    image={
                      <AspectRatio ratio={2 / 1} pos="relative" width="100%">
                        <Image
                          fill
                          sizes="100%"
                          src={`${post.imageUrl}?w=800`}
                          alt="" // Treat images as decorative
                          placeholder="blur"
                          blurDataURL={blogBlurDataUrl}
                          priority={index < 3}
                          style={{
                            objectFit: "cover",
                          }}
                        />
                      </AspectRatio>
                    }
                  />
                </FadeIn>
              ))}
            </SimpleGrid>

            <Flex w="100%" justify="center" mt="biggest">
              {filteredPosts && pageBlogCount > filteredPosts.length && (
                <Box ref={loadMoreRef}>{loadingState.status === "loading-more-posts" && <LoadingSpinner />}</Box>
              )}
            </Flex>

            {/* Include hidden pagination links for SEO */}
            <VisuallyHidden>
              {Array.from({ length: Math.ceil(categoryBlogCount / blogsPerPageLimit) }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key -- This is a static list of pages, so it's safe to use the index as the key.
                <Link key={index + 1} href={`/blog/category/${activeCategory.slug}/page/${index + 1}`}>
                  Page {index + 1}
                </Link>
              ))}
            </VisuallyHidden>
          </Box>
        </Flex>
      </Section>
    </Page>
  );
}
